import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import Button from '../../components/Button'
import PaginationFooter from '../../components/PaginationFooter'
import Modal from '../../components/Modal'
import GenericModal from '../../components/Modal/GenericModal'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import EditIcon from '../../assets/icons/pencil-blue.svg'
import { colors } from '../../const/colors'
import { Trash2, Table2, Power, Key } from 'lucide-react'

import {
  Container,
  Header,
  TitleWrapper,
  TitleIcon,
  TitleContent,
  Title,
  HeaderSubtitle,
  Subheader,
  SearchWrapper,
  Input,
  Table,
  Separator,
  TableHeader,
  TableHeaderData,
  TableBody,
  TableContainer,
  TableBodyRow,
  TableBodyCell,
  TitleTable,
  IconsWrapper,
  Icon,
  TableUsers
} from './styles'

const ITEMS_PER_PAGE = 7 // Adjust as needed

export const AdminManagement = ({
  entityType = 'Entidad',
  columns = [],
  apiEndpoint = '',
  FormComponent,
  gridTemplateColumns,
  renderRowDetail,
  searchKeys,
  singularEntityName,
  headerSubtitle = '',
  HeaderIcon = Table2
}) => {
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [editingEntity, setEditingEntity] = useState(null)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null })
  const [toggleModal, setToggleModal] = useState({ isOpen: false, item: null, targetState: true })
  const [resetPasswordModal, setResetPasswordModal] = useState({ isOpen: false, item: null })
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' })

  const currentUser = useSelector(state => state.user?.data)

  const modalRef = useRef()
  const isMounted = useRef(true)
  
  useEffect(() => {
    return () => { isMounted.current = false }
  }, [])

  const { fetchEntities, createEntity, updateEntity, deleteEntity, resetPassword } = useAdminAPI()

  const loadData = async () => {
    if (!apiEndpoint) return
    try {
      const result = await fetchEntities(apiEndpoint)
      if (isMounted.current) {
        const items = Array.isArray(result) ? result : result.data || []
        setData(items)
        setFilteredData(items)
      }
    } catch (error) {
      console.error('Failed to load data', error)
    }
  }

  useEffect(() => {
    loadData()
  }, [apiEndpoint])

  useEffect(() => {
    if (!search) {
      setFilteredData(data)
    } else {
      const lowerSearch = search.toLowerCase()
      setFilteredData(
        data.filter(item => {
          const keys = searchKeys || columns.map(c => c.key)
          return keys.some(key => {
            const val = item[key]
            return val && String(val).toLowerCase().includes(lowerSearch)
          })
        })
      )
    }
    setCurrentPage(1)
  }, [search, data, columns, searchKeys])

  const getId = (item) => {
    if (!item) return null
    // Map entity types to their specific ID fields based on user request and codebase patterns
    switch (entityType) {
      case 'Técnicos':
        return item.idTecnico || item.id || item._id || item.idUsuario
      case 'Instituciones':
        return item.idInstitucion || item.id || item._id
      case 'Oftalmólogos':
        return item.idOftalmologo || item.id || item._id || item.idUsuario
      case 'Administradores':
        return item.idAdmin || item.id || item._id || item.idUsuario
      case 'Administradores de Institución':
        return item.idUsuario || item.id || item._id
      default:
        return item.id || item._id || item.idUsuario || item.idTecnico || item.idOftalmologo || item.idInstitucion
    }
  }

  const handleDeleteClick = (item) => {
    setDeleteModal({ isOpen: true, item })
  }

  const handleToggleClick = (item) => {
    setToggleModal({ isOpen: true, item, targetState: !item.activo })
  }

  const handleResetPasswordClick = (item) => {
    setResetPasswordModal({ isOpen: false, item: null }) // temporarily close if open
    // Small timeout to ensure state settles before opening (useful if double clicking somehow)
    setTimeout(() => {
      setResetPasswordModal({ isOpen: true, item })
    }, 0)
  }

  const onConfirmDelete = async () => {
    const item = deleteModal.item
    if (!item) return

    const id = getId(item)
    try {
      await deleteEntity(apiEndpoint, id)
      await loadData()
      setDeleteModal({ isOpen: false, item: null })
    } catch (error) {
      setDeleteModal({ isOpen: false, item: null })
      if (entityType === 'Instituciones') {
        setAlertModal({
          isOpen: true,
          title: 'No puede borrarse esta institución',
          message: 'No pueden borrarse instituciones sin antes eliminar sus nodos de captura. Elimine primero sus nodos de captura e inténtelo nuevamente.'
        })
        return
      }
      if (entityType === 'Técnicos') {
        const fullName = [item?.nombre, item?.apellido].filter(Boolean).join(' ').trim() || 'seleccionado'
        const backendMessage = (error?.backendMessage || '').toLowerCase()
        const hasCapturedStudies = error?.status === 400 && backendMessage.includes('estudios asociados')

        if (hasCapturedStudies) {
          setAlertModal({
            isOpen: true,
            title: 'No se puede eliminar al técnico',
            message: `El técnico ${fullName} no puede eliminarse porque tiene estudios capturados a su nombre.`
          })
          return
        }
      }
      if (entityType === 'Oftalmólogos') {
        const fullName = [item?.nombre, item?.apellido].filter(Boolean).join(' ').trim() || 'seleccionado'
        const backendMessage = (error?.backendMessage || '').toLowerCase()
        const hasAssociatedStudies = error?.status === 400 && backendMessage.includes('informes asociados')

        if (hasAssociatedStudies) {
          setAlertModal({
            isOpen: true,
            title: 'No se puede eliminar al oftalmólogo',
            message: `El oftalmólogo ${fullName} no puede eliminarse porque tiene estudios informados.`
          })
          return
        }
      }
      setAlertModal({ isOpen: true, title: 'Error', message: 'Error al eliminar' })
    }
  }

  const onConfirmToggle = async () => {
    const item = toggleModal.item
    const targetState = toggleModal.targetState
    if (!item) return

    const id = getId(item)
    try {
      await updateEntity(apiEndpoint, `${id}/activo`, { activo: targetState })
      await loadData()
      setToggleModal({ isOpen: false, item: null, targetState: true })
    } catch (error) {
      setToggleModal({ isOpen: false, item: null, targetState: true })
      setAlertModal({
        isOpen: true,
        title: 'Error al cambiar estado',
        message: error?.backendMessage || 'Ocurrió un error al intentar cambiar el estado del registro.'
      })
    }
  }

  const onConfirmResetPassword = async () => {
    const item = resetPasswordModal.item
    if (!item) return

    const id = getId(item)
    try {
      await resetPassword(id)
      setResetPasswordModal({ isOpen: false, item: null })
      setAlertModal({
        isOpen: true,
        title: 'Contraseña blanqueada',
        message: `La contraseña se blanqueó correctamente y se ha enviado por correo a ${item.email || 'el usuario'}.`
      })
    } catch (error) {
      setResetPasswordModal({ isOpen: false, item: null })
      setAlertModal({
        isOpen: true,
        title: 'Error al blanquear contraseña',
        message: error?.backendMessage || 'Ocurrió un error al intentar blanquear la contraseña.'
      })
    }
  }

  const handleEdit = (item) => {
    setEditingEntity(item)
    modalRef.current.show()
  }

  const handleCreate = () => {
    setEditingEntity(null)
    modalRef.current.show()
  }

  const handleSave = async (formData) => {
    try {
      if (editingEntity) {
        const id = getId(editingEntity)
        await updateEntity(apiEndpoint, id, formData)
      } else {
        await createEntity(apiEndpoint, formData)
      }
      modalRef.current.hide()
      await loadData()
    } catch (error) {
      const message = error.backendMessage || 'Error al guardar el registro. Verificá los datos e intentá nuevamente.'
      setAlertModal({ isOpen: true, title: 'Error al guardar', message })
    }
  }

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE)
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
  const institutionName = deleteModal.item?.nombre || 'seleccionada'
  const technicianName = [deleteModal.item?.nombre, deleteModal.item?.apellido]
    .filter(Boolean)
    .join(' ')
    .trim() || 'seleccionado'
  const ophthalmologistName = [deleteModal.item?.nombre, deleteModal.item?.apellido]
    .filter(Boolean)
    .join(' ')
    .trim() || 'seleccionado'
  const deleteModalTitle = entityType === 'Instituciones'
    ? 'Eliminar la institución'
    : entityType === 'Técnicos'
      ? 'Eliminar técnico'
      : entityType === 'Oftalmólogos'
        ? 'Eliminar oftalmólogo'
        : 'Eliminar elemento'
  const deleteModalDescription = entityType === 'Instituciones'
    ? `¿Está seguro de eliminar la institución ${institutionName}?`
    : entityType === 'Técnicos'
      ? `¿Está seguro de que quiere eliminar al técnico ${technicianName}?`
      : entityType === 'Oftalmólogos'
        ? `¿Está seguro de que quiere eliminar al oftalmólogo ${ophthalmologistName}?`
        : '¿Está seguro de eliminar este elemento?'

  const toggleModalDescription = entityType === 'Instituciones'
    ? `¿Está seguro de que quiere ${toggleModal.targetState ? 'activar' : 'desactivar'} la institución ${toggleModal.item?.nombre || 'seleccionada'}?`
    : entityType === 'Técnicos'
      ? `¿Está seguro de que quiere ${toggleModal.targetState ? 'activar' : 'desactivar'} al técnico ${[toggleModal.item?.nombre, toggleModal.item?.apellido].filter(Boolean).join(' ').trim() || 'seleccionado'}?`
      : entityType === 'Oftalmólogos'
        ? `¿Está seguro de que quiere ${toggleModal.targetState ? 'activar' : 'desactivar'} al oftalmólogo ${[toggleModal.item?.nombre, toggleModal.item?.apellido].filter(Boolean).join(' ').trim() || 'seleccionado'}?`
        : `¿Está seguro de que quiere ${toggleModal.targetState ? 'activar' : 'desactivar'} a este administrador?`

  const getResetPasswordDescription = () => {
    const item = resetPasswordModal.item
    if (!item) return ''
    const fullName = [item.nombre, item.apellido].filter(Boolean).join(' ').trim()
    const role = entityType === 'Administradores'
      ? 'administrador'
      : entityType === 'Técnicos'
        ? 'técnico'
        : entityType === 'Oftalmólogos' ? 'oftalmólogo' : 'usuario'

    return `Se generará una nueva contraseña temporal para el ${role} ${fullName} (Usuario: ${item.username}) de forma inmediata. La credencial ingresada previamente quedará inhabilitada. La nueva contraseña se enviará a ${item.email || 'su correo'}. ¿Está seguro?`
  }

  return (
    <Container>
      <Header>
        <TitleWrapper>
          <TitleIcon>
            <HeaderIcon size={40} color={colors.primary} strokeWidth={2.1} aria-hidden='true' />
          </TitleIcon>
          <TitleContent>
            <Title> Gestión de {entityType} </Title>
            {headerSubtitle && (
              <HeaderSubtitle>{headerSubtitle}</HeaderSubtitle>
            )}
          </TitleContent>
        </TitleWrapper>
      </Header>

      <Subheader>
        <SearchWrapper onSubmit={(e) => e.preventDefault()}>
          <Input
            placeholder={`Buscar ${singularEntityName}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrapper>
        <Button color='primary' onClick={handleCreate}> + Agregar {singularEntityName}</Button>
      </Subheader>

      <Table>

        {entityType === 'Instituciones'
          ? (
            <TableBody>
              {paginatedData.map((item, index) => {
                const isInactive = item.activo === false
                return (
                  <div key={getId(item) || index} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <TableContainer>
                      <TableBodyRow gridTemplateColumns={gridTemplateColumns}>
                        {columns.map((col, colIndex) => (
                          <TableBodyCell key={colIndex} style={{ opacity: isInactive ? 0.6 : 1 }}>
                            {col.render ? col.render(item) : item[col.key]}
                          </TableBodyCell>
                        ))}
                        <IconsWrapper>
                          <Power
                            size={18}
                            color={isInactive ? colors.error : colors.success}
                            style={{ cursor: 'pointer' }}
                            aria-label={isInactive ? 'Activar' : 'Desactivar'}
                            onClick={() => handleToggleClick(item)}
                          />
                          <Icon src={EditIcon} alt='edit' onClick={() => handleEdit(item)} />
                          <Trash2
                            size={18}
                            color={colors.error}
                            style={{ cursor: 'pointer' }}
                            aria-label='remove'
                            onClick={() => handleDeleteClick(item)}
                          />
                        </IconsWrapper>
                      </TableBodyRow>
                      {renderRowDetail && renderRowDetail(item)}
                    </TableContainer>
                  </div>
                )
              })}
              {paginatedData.length === 0 && <div style={{ padding: 20, textAlign: 'center' }}>No se encontraron datos</div>}
            </TableBody>
            )
          : (
            <TableUsers>
              <TitleTable> {entityType} registrados ({filteredData.length}) </TitleTable>
              <TableHeader gridTemplateColumns={gridTemplateColumns}>
                {columns.map((col, colIndex) => (
                  <TableHeaderData key={colIndex}>
                    {col.label}
                  </TableHeaderData>
                ))}
                <TableHeaderData>Acciones</TableHeaderData>
              </TableHeader>
              <Separator />
              {paginatedData.map((item, index) => {
                const isInactive = item.activo === false
                const isCurrentUser = currentUser?.idUsuario === getId(item) || currentUser?.idAdmin === getId(item)
                return (
                  <div key={getId(item) || index} style={{ display: 'flex', flexDirection: 'column' }}>
                    <TableBodyRow gridTemplateColumns={gridTemplateColumns}>
                      {columns.map((col, colIndex) => (
                        <TableBodyCell key={colIndex} style={{ opacity: isInactive ? 0.6 : 1 }}>
                          {col.render ? col.render(item) : item[col.key]}
                        </TableBodyCell>
                      ))}
                      <IconsWrapper>
                        {!isCurrentUser && (
                          <Power
                            size={18}
                            color={isInactive ? colors.error : colors.success}
                            style={{ cursor: 'pointer' }}
                            aria-label={isInactive ? 'Activar' : 'Desactivar'}
                            onClick={() => handleToggleClick(item)}
                          />
                        )}
                        <Icon src={EditIcon} alt='edit' onClick={() => handleEdit(item)} />
                        {entityType !== 'Instituciones' && (
                          <Key
                            size={18}
                            color={colors.primary}
                            style={{ cursor: 'pointer', marginLeft: '2px', marginRight: '2px' }}
                            aria-label='Blanquear contraseña'
                            title='Blanquear contraseña'
                            onClick={() => handleResetPasswordClick(item)}
                          />
                        )}
                        <Trash2
                          size={18}
                          color={colors.error}
                          style={{ cursor: 'pointer' }}
                          aria-label='remove'
                          onClick={() => handleDeleteClick(item)}
                        />
                      </IconsWrapper>
                    </TableBodyRow>
                    <Separator />
                    {renderRowDetail && renderRowDetail(item)}
                  </div>
                )
              })}
              {paginatedData.length === 0 && <div style={{ padding: 20, textAlign: 'center' }}>No se encontraron datos</div>}
            </TableUsers>
            )}

        {totalPages > 1 && (
          <PaginationFooter
            currentPage={currentPage}
            listOfPages={pageList}
            handleChangePage={setCurrentPage}
          />
        )}
      </Table>

      <Modal 
        focus 
        ref={modalRef} 
        close={() => modalRef.current.hide()}
        closeOnBackdropClick={false}
        closeOnEscape={false}
      >
        {FormComponent && (
          <FormComponent
            initialData={editingEntity}
            onSubmit={handleSave}
            onCancel={() => modalRef.current.hide()}
          />
        )}
      </Modal>

      <GenericModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        title={deleteModalTitle}
        description={deleteModalDescription}
        withInput={false}
        onSave={onConfirmDelete}
        saveButtonText='Eliminar'
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: colors.error, text: '#fff', hover: '#b91c1c' }}
      />

      <GenericModal
        isOpen={toggleModal.isOpen}
        onClose={() => setToggleModal({ isOpen: false, item: null, targetState: true })}
        title={toggleModal.targetState ? 'Activar registro' : 'Desactivar registro'}
        description={toggleModalDescription}
        withInput={false}
        onSave={onConfirmToggle}
        saveButtonText={toggleModal.targetState ? 'Activar' : 'Desactivar'}
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: toggleModal.targetState ? colors.success : colors.warning, text: '#fff', hover: toggleModal.targetState ? '#15803d' : '#854d0e' }}
      />

      <GenericModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        description={alertModal.message}
        withInput={false}
        onSave={() => setAlertModal({ ...alertModal, isOpen: false })}
        saveButtonText='Aceptar'
        showCancelButton={false}
      />

      <GenericModal
        isOpen={resetPasswordModal.isOpen}
        onClose={() => setResetPasswordModal({ isOpen: false, item: null })}
        title='Blanquear contraseña'
        description={getResetPasswordDescription()}
        withInput={false}
        onSave={onConfirmResetPassword}
        saveButtonText='Generar y Enviar'
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: colors.primary, text: '#fff', hover: '#1e40af' }}
      />
    </Container>
  )
}

export default AdminManagement
